// src/pages/CartPage.jsx
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, Package, CreditCard, AlertCircle } from 'lucide-react';
import { useCartStore } from '../store/cartStore';
import { useAuthStore } from '../store/authStore';
import { useState } from 'react';

export const CartPage = () => {
  const { 
    items, 
    removeItem, 
    updateQuantity, 
    getTotalPrice, 
    clearCart,
    getGroupedItems,
    removePackage 
  } = useCartStore();
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const [processingPackage, setProcessingPackage] = useState(null);

  // دریافت آیتم‌های گروه‌بندی شده
  const { packages, regularItems } = getGroupedItems();
  const totalPrice = getTotalPrice();
  const totalItems = items.reduce((acc, item) => acc + item.quantity, 0);

  const handleCheckout = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/cart' } });
      return;
    }
    navigate('/checkout');
  };

  const handleRemovePackage = (packageId, packageName) => {
    if (window.confirm(`آیا از حذف پکیج "${packageName}" و همه محصولات آن اطمینان دارید؟`)) {
      removePackage(packageId);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="container mx-auto px-4 text-center">
          <ShoppingBag className="w-24 h-24 text-gray-300 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">سبد خرید خالی است</h2>
          <p className="text-gray-600 mb-8">محصولی به سبد خرید اضافه نشده است</p>
          <Link to="/products" className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition">
            مشاهده محصولات
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">سبد خرید</h1>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Cart Items Section */}
          <div className="lg:w-2/3 space-y-4">
            
            {/* نمایش پکیج‌ها */}
            {packages.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    <Package className="w-5 h-5 text-blue-600" />
                    پکیج‌های انتخابی
                  </h2>
                  <span className="text-xs text-gray-500">
                    {packages.length} پکیج
                  </span>
                </div>
                
                {packages.map((pkg) => (
                  <div key={pkg.packageId} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 overflow-hidden">
                    {/* هدر پکیج */}
                    <div className="bg-blue-100 px-4 py-3 border-b border-blue-200 flex justify-between items-center flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <Package className="w-5 h-5 text-blue-700" />
                        <h3 className="font-bold text-blue-800">{pkg.packageName}</h3>
                        <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full">
                          {pkg.items.length} محصول
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-blue-700 font-bold">
                          مجموع: {pkg.totalPrice.toLocaleString()} تومان
                        </span>
                        <button
                          onClick={() => handleRemovePackage(pkg.packageId, pkg.packageName)}
                          className="text-red-500 hover:text-red-700 p-1 transition"
                          title="حذف کل پکیج"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {/* محصولات داخل پکیج */}
                    <div className="divide-y divide-blue-100">
                      {pkg.items.map((item) => (
                        <div key={`${item.id}-${item.packageId}`} className="p-4 flex gap-4 bg-white/50">
                          {/* تصویر محصول */}
                          <img
                            src={item.main_image || '/placeholder-product.jpg'}
                            alt={item.name}
                            className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/placeholder-product.jpg';
                            }}
                          />
                          
                          {/* اطلاعات محصول */}
                          <div className="flex-grow">
                            <div className="flex justify-between items-start flex-wrap gap-2">
                              <div>
                                <h4 className="font-bold text-gray-800">{item.name}</h4>
                                <div className="flex items-center gap-3 mt-1 flex-wrap">
                                  <span className="text-xs text-gray-500">
                                    کد محصول: {item.id}
                                  </span>
                                  {item.isPackageItem && (
                                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                      شامل پکیج
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="text-left">
                                <div className="font-bold text-blue-600">
                                  {item.final_price?.toLocaleString()} تومان
                                </div>
                                <div className="text-xs text-gray-400">
                                  قیمت واحد
                                </div>
                              </div>
                            </div>
                            
                            {/* کنترل تعداد و حذف */}
                            <div className="flex justify-between items-center mt-3">
                              <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                                <button
                                  onClick={() => updateQuantity(item.id, item.quantity - 1, item.packageId)}
                                  className="p-1 hover:bg-white rounded w-7 h-7 flex items-center justify-center transition"
                                  disabled={processingPackage === item.packageId}
                                >
                                  <Minus className="w-4 h-4" />
                                </button>
                                <span className="w-8 text-center font-medium">{item.quantity}</span>
                                <button
                                  onClick={() => updateQuantity(item.id, item.quantity + 1, item.packageId)}
                                  className="p-1 hover:bg-white rounded w-7 h-7 flex items-center justify-center transition"
                                  disabled={processingPackage === item.packageId}
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                              </div>
                              
                              <button
                                onClick={() => removeItem(item.id, item.packageId)}
                                className="text-red-400 hover:text-red-600 text-sm flex items-center gap-1 transition"
                              >
                                <Trash2 className="w-4 h-4" />
                                حذف
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* فوتر پکیج */}
                    <div className="bg-blue-100/50 px-4 py-2 text-xs text-blue-600 flex justify-between">
                      <span>✓ محصولات این پکیج با تخفیف ویژه ارائه شده است</span>
                      <span>تعداد کل: {pkg.items.reduce((sum, i) => sum + i.quantity, 0)} عدد</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* محصولات تکی (غیر پکیج) */}
            {regularItems.length > 0 && (
              <div className="space-y-4">
                {packages.length > 0 && (
                  <div className="flex items-center justify-between mt-6">
                    <h2 className="text-lg font-bold text-gray-800">محصولات تکی</h2>
                    <span className="text-xs text-gray-500">
                      {regularItems.length} محصول
                    </span>
                  </div>
                )}
                
                {regularItems.map((item) => (
                  <div key={item.id} className="bg-white rounded-xl p-4 flex gap-4 shadow-sm hover:shadow-md transition">
                    {/* تصویر محصول */}
                    <img
                      src={item.main_image || '/placeholder-product.jpg'}
                      alt={item.name}
                      className="w-24 h-24 object-cover rounded-lg"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/placeholder-product.jpg';
                      }}
                    />
                    
                    {/* اطلاعات محصول */}
                    <div className="flex-grow">
                      <Link to={`/products/${item.slug || item.id}`}>
                        <h3 className="font-bold text-gray-900 mb-1 hover:text-blue-600 transition">{item.name}</h3>
                      </Link>
                      <p className="text-sm text-gray-500 mb-2">{item.category_name || 'محصول'}</p>
                      <div className="text-blue-600 font-bold">
                        {item.final_price?.toLocaleString()} تومان
                      </div>
                    </div>

                    {/* دکمه‌های کنترلی */}
                    <div className="flex flex-col items-end justify-between gap-3">
                      <button
                        onClick={() => removeItem(item.id)}
                        className="text-red-500 hover:text-red-700 p-1 transition"
                        title="حذف از سبد خرید"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                      
                      <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="p-1 hover:bg-white rounded w-7 h-7 flex items-center justify-center transition"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-medium">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="p-1 hover:bg-white rounded w-7 h-7 flex items-center justify-center transition"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* دکمه پاک کردن کل سبد */}
            {items.length > 0 && (
              <div className="flex justify-end mt-4">
                <button
                  onClick={clearCart}
                  className="text-red-600 hover:text-red-700 text-sm font-medium flex items-center gap-1 transition"
                >
                  <Trash2 className="w-4 h-4" />
                  پاک کردن سبد خرید
                </button>
              </div>
            )}
          </div>

          {/* Summary Section - سایدبار سمت راست */}
          <div className="lg:w-1/3">
            <div className="bg-white rounded-xl p-6 shadow-lg sticky top-24">
              <h2 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2">خلاصه سفارش</h2>
              
              {/* اطلاعات آماری */}
              <div className="space-y-3 mb-4">
                <div className="flex justify-between text-gray-600">
                  <span>تعداد کل کالاها</span>
                  <span className="font-medium">{totalItems} عدد</span>
                </div>
                
                {packages.length > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>تعداد پکیج‌ها</span>
                    <span className="font-medium text-blue-600">{packages.length} پکیج</span>
                  </div>
                )}
                
                {regularItems.length > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>محصولات تکی</span>
                    <span className="font-medium">{regularItems.length} محصول</span>
                  </div>
                )}
                
                <div className="flex justify-between text-gray-600">
                  <span>جمع کل</span>
                  <span>{totalPrice.toLocaleString()} تومان</span>
                </div>
              </div>

              {/* صرفه‌جویی (اگر پکیج وجود داره) */}
              {packages.length > 0 && (
                <div className="bg-green-50 p-3 rounded-lg mb-4 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-green-700">
                    <p className="font-bold mb-1">شما با خرید پکیج صرفه‌جویی می‌کنید!</p>
                    <p>محصولات داخل پکیج با تخفیف ویژه ارائه شده‌اند</p>
                  </div>
                </div>
              )}

              {/* جمع نهایی */}
              <div className="border-t pt-4 mb-6">
                <div className="flex justify-between text-lg font-bold">
                  <span>مبلغ قابل پرداخت</span>
                  <span className="text-blue-600 text-xl">
                    {totalPrice.toLocaleString()} تومان
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  قیمت‌ها به تومان می‌باشد
                </p>
              </div>

              {/* دکمه اقدام به پرداخت */}
              <button
                onClick={handleCheckout}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-bold transition flex items-center justify-center gap-2"
              >
                <CreditCard className="w-5 h-5" />
                ادامه فرآیند خرید
              </button>

              {/* لینک بازگشت */}
              <Link
                to="/products"
                className="block text-center mt-4 text-gray-500 hover:text-gray-700 text-sm transition"
              >
                ← بازگشت به فروشگاه
              </Link>
              
              {/* اطلاعات اضافی */}
              <div className="mt-6 pt-4 border-t text-xs text-gray-400 text-center space-y-1">
                <p>✓ گارانتی اصالت و سلامت فیزیکی کالا</p>
                <p>✓ امکان بازگشت کالا تا ۷ روز</p>
                <p>✓ پرداخت امن از طریق درگاه‌های معتبر</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};